"use client";

import { useEffect, useId, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Chassis parts for the /desktop retro terminal hero: a chunky CRT on a tilt
 * stand, a keyboard with keys that physically press while the terminal types,
 * a corded desk mouse, scanlines, and the classic arrow pointer. All drawn
 * from borders + marketing tokens (cream body, tan borders, navy screen); the
 * soft blue shadow matches the dataset folders and root landing cards.
 */

export type PowerLedState = "off" | "on" | "blink";

function PowerLed({ state }: { state: PowerLedState }) {
  return (
    <span
      className={cn(
        "size-2.5 rounded-full border border-black/10",
        state === "off" && "bg-marketing-border",
        state === "on" && "bg-marketing-accent-green",
        state === "blink" && "bg-marketing-accent-red animate-pulse",
      )}
    />
  );
}

export function Scanlines() {
  const patternId = useId();
  return (
    <svg
      className="pointer-events-none absolute inset-0 z-20 h-full w-full opacity-[0.07]"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id={patternId}
          width="4"
          height="3"
          patternUnits="userSpaceOnUse"
        >
          <line
            x1="0"
            y1="0.5"
            x2="4"
            y2="0.5"
            stroke="var(--color-marketing-navy)"
            strokeWidth="1"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
}

interface CrtChassisProps {
  children: React.ReactNode;
  led?: PowerLedState;
  /** Background classes for the screen surface. */
  screenClassName?: string;
  keyboardActive?: boolean;
}

/** CRT monitor on a tilt stand with a keyboard slab: screen 320x200. */
export function CrtChassis({
  children,
  led = "on",
  screenClassName = "bg-marketing-navy",
  keyboardActive = false,
}: CrtChassisProps) {
  return (
    <div className="w-[380px] select-none">
      <div className="border-marketing-border bg-marketing-cream rounded-[22px] border p-4 pb-3 shadow-[0_14px_40px_rgba(0,6,183,0.18),_0_4px_10px_rgba(0,6,183,0.08)]">
        <div className="border-marketing-border bg-marketing-cream-light rounded-[16px] border p-3">
          <div
            className={cn(
              "relative h-[200px] w-[320px] overflow-hidden rounded-[12px]",
              screenClassName,
            )}
          >
            {children}
            <Scanlines />
          </div>
        </div>
        <div className="mt-2.5 flex items-center justify-between px-1">
          <span className="text-marketing-text-muted font-mono text-[10px] tracking-[2px] uppercase">
            Terac
          </span>
          <div className="flex items-center gap-2.5">
            <span className="bg-marketing-border h-[5px] w-7 rounded-full" />
            <span className="bg-marketing-border h-[5px] w-7 rounded-full" />
            <PowerLed state={led} />
          </div>
        </div>
      </div>
      <div className="mx-auto h-3.5 w-28 bg-[var(--color-marketing-border)] opacity-80 [clip-path:polygon(12%_0,88%_0,100%_100%,0_100%)]" />
      <div className="bg-marketing-border mx-auto h-2 w-44 rounded-[4px]" />
      <Keyboard className="mt-3.5" active={keyboardActive} />
    </div>
  );
}

const KEY_ROWS: { id: string; keys: { id: string }[] }[] = [13, 13, 12].map(
  (count, row) => ({
    id: `row${row}`,
    keys: Array.from({ length: count }, (_, k) => ({ id: `r${row}k${k}` })),
  }),
);

/** Pseudo-random touch-typing order; "space" presses the spacebar. */
const PRESS_SEQUENCE = [
  "r1k3",
  "r0k5",
  "r2k7",
  "r1k8",
  "r0k2",
  "space",
  "r1k5",
  "r2k3",
  "r0k9",
  "r1k1",
  "r2k9",
  "space",
  "r0k7",
  "r1k10",
  "r2k5",
  "r0k11",
];

const PRESS_INTERVAL_MS = 110;

export function Keyboard({
  className,
  active = false,
}: {
  className?: string;
  active?: boolean;
}) {
  const [pressStep, setPressStep] = useState(0);

  useEffect(() => {
    if (!active) {
      return;
    }
    const timer = setInterval(() => {
      setPressStep((v) => (v + 1) % PRESS_SEQUENCE.length);
    }, PRESS_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [active]);

  const pressedId = active ? PRESS_SEQUENCE[pressStep] : null;

  const keyClass = (id: string) =>
    cn(
      "border-marketing-border-light bg-marketing-cream-light rounded-[4px] border transition-[background-color,border-color,translate] duration-75",
      pressedId === id &&
        "bg-marketing-border translate-y-[1.5px] border-marketing-border",
    );

  return (
    <div
      className={cn(
        "border-marketing-border bg-marketing-cream mx-auto w-[316px] rounded-[12px] border px-3 py-2.5 shadow-[0_8px_24px_rgba(0,6,183,0.12)]",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-1.5">
        {KEY_ROWS.map((row) => (
          <div key={row.id} className="flex justify-center gap-1.5">
            {row.keys.map((key) => (
              <span key={key.id} className={cn("size-4", keyClass(key.id))} />
            ))}
          </div>
        ))}
        <span className={cn("h-4 w-32", keyClass("space"))} />
      </div>
    </div>
  );
}

/** One-button corded desk mouse; the cord curls up toward the machine. */
export function RetroMouse({ className }: { className?: string }) {
  return (
    <div className={cn("relative", className)}>
      <svg
        width="100"
        height="80"
        viewBox="0 0 100 80"
        fill="none"
        className="absolute -top-[64px] -left-[68px]"
        aria-hidden="true"
      >
        <path
          d="M96 76 C 78 32, 38 62, 4 8"
          stroke="var(--color-marketing-border)"
          strokeWidth="3.5"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
      <div className="border-marketing-border bg-marketing-cream relative h-[86px] w-[64px] rounded-t-[30px] rounded-b-[20px] border shadow-[0_8px_24px_rgba(0,6,183,0.12)]">
        <div className="bg-marketing-border absolute top-[26px] left-0 h-px w-full" />
      </div>
    </div>
  );
}

/** Classic arrow pointer, cream with a navy outline. */
export function CursorPointer({ className }: { className?: string }) {
  return (
    <svg
      width="18"
      height="20"
      viewBox="0 0 16 18"
      fill="none"
      className={className}
      aria-hidden="true"
    >
      <path
        d="M2 1 L2 14.6 L5.6 11.4 L7.8 16.4 L10.2 15.3 L8 10.6 L12.8 10.4 Z"
        fill="var(--color-marketing-cream)"
        stroke="var(--color-marketing-navy)"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}
